import type { ProjectGraphNode, ProjectGraphRuleFinding } from '@avisoma/shared';
import { formatShortId } from '../../components/ID';

export type EvidenceLink = { key: string; to: string; label: string };

const reconstructedPath = (id: string) => `/reconstructed-documents/${encodeURIComponent(id)}`;
const pagePath = (id: string) => `/document-pages/${encodeURIComponent(id)}`;

/** A `doc:` node id stores the reconstructed document id, URI-encoded. */
function reconstructedId(nodeId: string): string | undefined {
  if (!nodeId.startsWith('doc:')) return undefined;
  const raw = nodeId.slice('doc:'.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Links for one finding's evidence. A document node opens the reconstructed document (the statement). Each page opens
 * the page. A `doc:` id with no node still opens the reconstructed document. Other ids with no pages add no link.
 */
export function evidenceLinks(
  evidence: ProjectGraphRuleFinding['evidence'],
  nodesById: ReadonlyMap<string, ProjectGraphNode>,
): EvidenceLink[] {
  return (evidence ?? []).flatMap((entry) => {
    const node = nodesById.get(entry.nodeId);
    const reconstructedDocumentId =
      node?.kind === 'document' ? node.reconstructedDocumentId : reconstructedId(entry.nodeId);
    const title = node?.kind === 'document' ? node.title.trim() : '';
    const documentLink: EvidenceLink[] =
      reconstructedDocumentId === undefined
        ? []
        : [
            {
              key: entry.nodeId,
              to: reconstructedPath(reconstructedDocumentId),
              label: title === '' ? formatShortId(reconstructedDocumentId) : title,
            },
          ];
    const pageLinks = (entry.pageIds ?? []).map((pageId) => ({
      key: `${entry.nodeId}:${pageId}`,
      to: pagePath(pageId),
      label: `page ${pageId.slice(0, 8)}`,
    }));
    return [...documentLink, ...pageLinks];
  });
}
